// Core
import {
  Box,
  Image,
  Divider,
  Button,
  Grid,
  Text,
  Flex,
  useColorMode,
  useColorModeValue,
} from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';

// Assets
import { formatDateTime, parseFullDate } from 'services/Utils';
import placeholderGiveaways from 'assets/img/placeholder-giveaways.jpg';
import { ReactComponent as Lines } from 'assets/svg/lines.svg';
import { ReactComponent as LinesWhite } from 'assets/svg/lines-white.svg';
import { ReactComponent as Prize1 } from 'assets/svg/prizes/prize-1.svg';
import { ReactComponent as Prize2 } from 'assets/svg/prizes/prize-2.svg';
import { ReactComponent as Prize3 } from 'assets/svg/prizes/prize-3.svg';
import { ReactComponent as DailyPrize } from 'assets/svg/prizes/daily.svg';
import { ReactComponent as PlaceholderPrize } from 'assets/svg/prizes/placeholder.svg';

// Components
import Card from 'components/Card/Card';
import Countdown from 'components/Timers/Countdown';

const PrizeItem = ({ indexPrize, title, firstAndLastName }) => {
  return (
    <Flex key={indexPrize} gap="8px" mb="16px">
      <Box
        borderRadius="8px"
        bg="rgba(9, 155, 255, 0.10)"
        w="32px"
        h="32px"
        p="4px"
      >
        {indexPrize + 1 === 1 ? (
          <Prize1 />
        ) : indexPrize + 1 === 2 ? (
          <Prize2 />
        ) : indexPrize + 1 === 3 ? (
          <Prize3 />
        ) : (
          <PlaceholderPrize style={{ width: '24px', height: '24px' }} />
        )}
      </Box>

      <Box>
        <Text variant="title-md" mb="4px">
          {indexPrize + 1}
          {indexPrize + 1 === 1
            ? 'st'
            : indexPrize + 1 === 2
            ? 'nd'
            : indexPrize + 1 === 3
            ? 'rd'
            : 'th'}{' '}
          Prize {title}
        </Text>
        <Text>{firstAndLastName}</Text>
      </Box>
    </Flex>
  );
};

const Giveaway = ({ item, type, userData }) => {
  const colorDate = useColorModeValue('black', '#9FABCB');
  const colorDivider = useColorModeValue(
    '1px solid #CBE7FF',
    '1px solid #262D47'
  );

  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const history = useHistory();
  const handleClick = (event, id) => {
    if (
      event.ctrlKey ||
      event.metaKey ||
      event.button === 1 ||
      item.promotionLink ||
      item.publicPromotionLink
    ) {
      return;
    }

    event.preventDefault();
    history.push(`/giveaways/${id}`);
  };

  return (
    <Card
      padding="0"
      overflow="hidden"
      cursor={item?.status === 'finished' ? 'cursor' : 'pointer'}
    >
      <a
        href={ userData ? item?.promotionLink : item?.publicPromotionLink ?? `/giveaways/${item?.id}`}
        onClick={(e) => handleClick(e, item?.id)}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 2,
          pointerEvents: item?.status === 'finished' ? 'none' : 'unset',
        }}
      />

      <Box position="relative">
        {type !== 'daily' &&
        <Image
          src={item?.image ?? placeholderGiveaways}
          w="100%"
          h={{ base: '172px' }}
          objectFit="cover"
        />
        }
        {type === 'upcoming' && (
          <Countdown
            fontSize="15px"
            endDate={item?.endDate}
            declareDate={item?.declareDate}
            p="0 6px"
          />
        )}
      </Box>

      <Box borderTop={ type !== 'daily' ? colorDivider : 'unset'} p="12px 12px 0px 12px">
        <Text variant="header" fontSize="16px" letterSpacing="0" isTruncated>
          {type !== 'daily' ? item?.title : '$1000 prize giveaway'}
        </Text>
        <Text
          variant="text-sm"
          fontWeight="500"
          color={colorDate}
          mt="8px"
          isTruncated
        >
          {type !== 'upcoming'
            ? formatDateTime(item?.endDate)
            : parseFullDate(item?.endDate)}{' '}
          {type === 'winners' ? '- Event complete' : ''}
        </Text>

        { type === 'upcoming' && (
          <Box mb="12px">
            {userData && (
              <Text
                mt="18px"
                variant="title-md"
                fontSize="13px"
                borderRadius="full"
                p="2px 8px"
                width="max-content"
                bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)"
                color="white"
                fontStyle="italic"
              >
                YOUR ENTRIES: {item?.entries}
              </Text>
            )}
          </Box>
        )}

        {type === 'winners' && (
          <>
            <Divider borderBottom={colorDivider} my="12px" />

            {(() => {
              // const maxPrizes = 3;
              const defaultPrize = {
                title: '',
                firstAndLastName: '',
              };
              const prizesList = item?.prizes ? [...item.prizes] : [];
              /* while (prizesList.length < maxPrizes) {
                prizesList.push(defaultPrize);
              }*/

              return prizesList.map((prize, indexPrize) => (
                <PrizeItem
                  key={indexPrize}
                  indexPrize={indexPrize}
                  title={prize?.title || defaultPrize?.title}
                  firstAndLastName={
                    prize?.winner
                      ? `${prize.winner.firstName} ${prize.winner.lastName ? prize.winner.lastName.charAt(0) + '.' : ''}`
                      : defaultPrize.firstAndLastName
                  }
                />
              ));
            })()}
          </>
        )}

        {type === 'daily' && (
          <>
            <Divider borderBottom={colorDivider} my="12px" />

            <Flex gap="8px" mb="24px">
              <Box
                borderRadius="8px"
                bg="rgba(9, 155, 255, 0.10)"
                w="32px"
                h="32px"
                p="4px"
              >
                  <DailyPrize />
              </Box>

              <Box>
                <Text variant="title-md" mb="4px" textTransform="uppercase">Winner:</Text>
                <Text>{item?.prizes[0]?.winner
                      ? `${item?.prizes[0].winner.firstName} ${item?.prizes[0].winner.lastName ? item?.prizes[0].winner.lastName.charAt(0) + '.' : ''}`
                      : 'Remaining prizes not awarded'}</Text>
              </Box>
            </Flex>
          </>
        )}

      </Box>

      {isDark ? (
        <Lines
          className="abstract-lines"
          style={{
            zIndex: 1,
            transform: 'scale(0.8)',
            left: '-50px',
            top: type !== 'upcoming' ? '50px' : '-50px',
            transform: 'scale(0.5) rotate(266deg)',
          }}
        />
      ) : (
        <LinesWhite
          className="abstract-lines"
          style={{
            opacity: '0.4',
            zIndex: 1,
            transform: 'scale(0.8)',
            left: '-50px',
            top: type !== 'upcoming' ? '50px' : '-50px',
            transform: 'scale(0.5) rotate(266deg)',
          }}
        />
      )}
    </Card>
  );
};

export default Giveaway;
